import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './session.jsx';
import { resetSessionCache } from './queryClient.js';
import { api } from '../lib/api.js';
import { AppShell } from './AppShell.jsx';
import { ErrorState, Spinner } from '../components/ui.jsx';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '../features/auth/AuthPages.jsx';
import { InvitePage } from '../features/auth/InvitePage.jsx';
import { OnboardingPage } from '../features/onboarding/OnboardingPage.jsx';

const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage.jsx'));
const TasksPage = lazy(() => import('../features/tasks/TasksPage.jsx'));
const TaskDetailPage = lazy(() => import('../features/tasks/TaskDetailPage.jsx'));
const WeeklyReviewPage = lazy(() => import('../features/tasks/WeeklyReviewPage.jsx'));
const ShoppingPage = lazy(() => import('../features/shopping/ShoppingPage.jsx'));
const PantryPage = lazy(() => import('../features/pantry/PantryPage.jsx'));
const MealsPage = lazy(() => import('../features/meals/MealsPage.jsx'));
const RecipePage = lazy(() => import('../features/meals/RecipePage.jsx'));
const FinancePage = lazy(() => import('../features/finance/FinancePage.jsx'));
const AgendaPage = lazy(() => import('../features/agenda/AgendaPage.jsx'));
const MaintenancePage = lazy(() => import('../features/maintenance/MaintenancePage.jsx'));
const AssetPage = lazy(() => import('../features/maintenance/AssetPage.jsx'));
const DocumentsPage = lazy(() => import('../features/documents/DocumentsPage.jsx'));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage.jsx'));
const InboxPage = lazy(() => import('../features/inbox/InboxPage.jsx'));
const ShareTargetPage = lazy(() => import('../features/inbox/ShareTargetPage.jsx'));
const HouseholdSettingsPage = lazy(() => import('../features/settings/HouseholdSettingsPage.jsx'));
const AccountSettingsPage = lazy(() => import('../features/settings/AccountSettingsPage.jsx'));
const MorePage = lazy(() => import('../features/settings/MorePage.jsx'));
const MailboxPage = lazy(() => import('../features/dev/MailboxPage.jsx'));
const AdminShell = lazy(() => import('../features/admin/AdminShell.jsx'));
const AdminOverviewPage = lazy(() => import('../features/admin/AdminOverviewPage.jsx'));
const CondominiumsPage = lazy(() => import('../features/admin/CondominiumsPage.jsx'));
const NewCondominiumPage = lazy(() => import('../features/admin/NewCondominiumPage.jsx'));
const CondominiumDetailPage = lazy(() => import('../features/admin/CondominiumDetailPage.jsx'));
const OrganizationsPage = lazy(() => import('../features/admin/OrganizationsPage.jsx'));
const PlansPage = lazy(() => import('../features/admin/PlansPage.jsx'));
const AdminsPage = lazy(() => import('../features/admin/AdminsPage.jsx'));
const IntegrationsPage = lazy(() => import('../features/admin/IntegrationsPage.jsx'));
const AuditPage = lazy(() => import('../features/admin/AuditPage.jsx'));
const CondoOnboardingPage = lazy(() => import('../features/admin/CondoOnboardingPage.jsx'));
const OperatorFinancePage = lazy(() => import('../features/admin/OperatorFinancePage.jsx'));
const AdminAntecipationPage = lazy(() =>
  import('../features/admin/AdminAntecipationPages.jsx').then((m) => ({ default: m.AdminAntecipationPage })),
);
const AdminLotPage = lazy(() =>
  import('../features/admin/AdminAntecipationPages.jsx').then((m) => ({ default: m.AdminLotPage })),
);
const CondoShell = lazy(() => import('../features/condo/CondoShell.jsx'));
const CondoIndexPage = lazy(() => import('../features/condo/CondoIndexPage.jsx'));
const JoinCondoPage = lazy(() => import('../features/condo/JoinCondoPage.jsx'));
const CondoHomePage = lazy(() => import('../features/condo/CondoHomePage.jsx'));
const StructurePage = lazy(() => import('../features/condo/StructurePage.jsx'));
const ResidentsPage = lazy(() => import('../features/condo/ResidentsPage.jsx'));
const VehiclesPage = lazy(() => import('../features/condo/VehiclesPage.jsx'));
const FinanceLayout = lazy(() => import('../features/condo-finance/FinanceLayout.jsx'));
const FinanceOverviewPage = lazy(() => import('../features/condo-finance/FinanceOverviewPage.jsx'));
const CondoEntriesPage = lazy(() => import('../features/condo-finance/EntriesPage.jsx'));
const CondoEntryDetailPage = lazy(() => import('../features/condo-finance/EntryDetailPage.jsx'));
const CondoAccountsPage = lazy(() => import('../features/condo-finance/AccountsPage.jsx'));
const ReconciliationPage = lazy(() => import('../features/condo-finance/ReconciliationPage.jsx'));
const CondoReportsPage = lazy(() => import('../features/condo-finance/ReportsPage.jsx'));
const CondoFinanceSettingsPage = lazy(() => import('../features/condo-finance/SettingsPage.jsx'));
const ResidentReportsPage = lazy(() => import('../features/condo-finance/ResidentReportsPage.jsx'));
const BillingLayout = lazy(() => import('../features/billing/BillingLayout.jsx'));
const BillingOverviewPage = lazy(() => import('../features/billing/BillingOverviewPage.jsx'));
const ChargesPage = lazy(() => import('../features/billing/ChargesPage.jsx'));
const ChargeDetailPage = lazy(() => import('../features/billing/ChargeDetailPage.jsx'));
const DelinquencyPage = lazy(() => import('../features/billing/DelinquencyPage.jsx'));
const AgreementsPage = lazy(() => import('../features/billing/AgreementsPage.jsx'));
const AntecipationPage = lazy(() =>
  import('../features/antecipation/AntecipationPages.jsx').then((m) => ({ default: m.AntecipationPage })),
);
const AntecipationLotPage = lazy(() =>
  import('../features/antecipation/AntecipationPages.jsx').then((m) => ({ default: m.AntecipationLotPage })),
);
const BillingSettingsPage = lazy(() => import('../features/billing/BillingSettingsPage.jsx'));
const MyChargesPage = lazy(() => import('../features/billing/MyChargesPage.jsx'));
const PurchasingLayout = lazy(() => import('../features/purchasing/common.jsx'));
const ListsPage = lazy(() =>
  import('../features/purchasing/ListsPages.jsx').then((m) => ({ default: m.ListsPage })),
);
const PurchasingOverviewPage = lazy(() =>
  import('../features/purchasing/ListsPages.jsx').then((m) => ({ default: m.PurchasingOverviewPage })),
);
const ListDetailPage = lazy(() =>
  import('../features/purchasing/ListsPages.jsx').then((m) => ({ default: m.ListDetailPage })),
);
const RfqsPage = lazy(() =>
  import('../features/purchasing/RfqPages.jsx').then((m) => ({ default: m.RfqsPage })),
);
const RfqDetailPage = lazy(() =>
  import('../features/purchasing/RfqPages.jsx').then((m) => ({ default: m.RfqDetailPage })),
);
const OrdersPage = lazy(() =>
  import('../features/purchasing/OrdersSuppliersPages.jsx').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('../features/purchasing/OrdersSuppliersPages.jsx').then((m) => ({ default: m.OrderDetailPage })),
);
const SuppliersPage = lazy(() =>
  import('../features/purchasing/OrdersSuppliersPages.jsx').then((m) => ({ default: m.SuppliersPage })),
);
const PublicQuotePage = lazy(() => import('../features/purchasing/PublicQuotePage.jsx'));
const ReservationsPage = lazy(() => import('../features/reservations/ReservationsPage.jsx'));
const ReservationsAdminPage = lazy(() => import('../features/reservations/ReservationsAdminPage.jsx'));
const MaintenanceLayout = lazy(() => import('../features/condo-ops/MaintenancePages.jsx'));
const MaintenanceDashboard = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.MaintenanceDashboard })),
);
const WorkOrdersPage = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.WorkOrdersPage })),
);
const WorkOrderDetailPage = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.WorkOrderDetailPage })),
);
const AssetsPage = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.AssetsPage })),
);
const AssetDetailPage = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.AssetDetailPage })),
);
const MaintenanceSettingsPage = lazy(() =>
  import('../features/condo-ops/MaintenancePages.jsx').then((m) => ({ default: m.MaintenanceSettingsPage })),
);
const IndicatorsPage = lazy(() => import('../features/condo-insights/IndicatorsPage.jsx'));
const OccurrencesPage = lazy(() =>
  import('../features/condo-ops/OccurrencesPages.jsx').then((m) => ({ default: m.OccurrencesPage })),
);
const OccurrenceDetailPage = lazy(() =>
  import('../features/condo-ops/OccurrencesPages.jsx').then((m) => ({ default: m.OccurrenceDetailPage })),
);
const AnnouncementsPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.AnnouncementsPage })),
);
const AnnouncementDetailPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.AnnouncementDetailPage })),
);
const CondoDocumentsPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.DocumentsPage })),
);
const ParcelsPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.ParcelsPage })),
);
const AssembliesPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.AssembliesPage })),
);
const AssemblyDetailPage = lazy(() =>
  import('../features/condo-portal/PortalPages.jsx').then((m) => ({ default: m.AssemblyDetailPage })),
);
const ServicesPage = lazy(() => import('../features/larelo/ServicesPage.jsx'));
const NewServiceRequestPage = lazy(() => import('../features/larelo/NewServiceRequestPage.jsx'));
const ServiceRequestPage = lazy(() => import('../features/larelo/ServiceRequestPage.jsx'));
const MaintenanceDaysPage = lazy(() => import('../features/larelo/MaintenanceDaysPage.jsx'));
const MaintenanceDayDetailPage = lazy(() => import('../features/larelo/MaintenanceDayDetailPage.jsx'));
const ProShell = lazy(() => import('../features/larelo/ProShell.jsx'));
const ProProfilePage = lazy(() => import('../features/larelo/ProProfilePage.jsx'));
const ProOpportunitiesPage = lazy(() => import('../features/larelo/ProOpportunitiesPage.jsx'));
const ProRequestPage = lazy(() => import('../features/larelo/ProRequestPage.jsx'));
const ProQuotesPage = lazy(() => import('../features/larelo/ProQuotesPage.jsx'));
const ProAgendaPage = lazy(() => import('../features/larelo/ProAgendaPage.jsx'));
const ProStatementPage = lazy(() => import('../features/larelo/ProStatementPage.jsx'));
const LeadFinderPage = lazy(() => import('../features/larelo/LeadFinderPage.jsx'));
const ProReviewsPage = lazy(() =>
  import('../features/larelo/ProScalePages.jsx').then((m) => ({ default: m.ProReviewsPage })),
);
const ProOwnSchedulePage = lazy(() =>
  import('../features/larelo/ProScalePages.jsx').then((m) => ({ default: m.ProOwnSchedulePage })),
);
const LareloAdminPage = lazy(() => import('../features/larelo/LareloAdminPage.jsx'));

function Splash() {
  return (
    <div className="auth-screen" aria-busy="true">
      <Spinner label="Carregando o Lar em Dia" />
    </div>
  );
}

function PageFallback() {
  return (
    <div className="state" aria-busy="true">
      <Spinner />
    </div>
  );
}

/** Exige login; sem lar ainda, leva ao onboarding. */
function RequireAuth({ children, allowNoHousehold }) {
  const { loading, user, households, platformRoles, condominiums, professionalStatus, error, retry } =
    useSession();
  const location = useLocation();
  if (loading) return <Splash />;
  if (error)
    return (
      <div className="auth-screen">
        <ErrorState error={error} onRetry={retry} />
      </div>
    );
  if (!user)
    return (
      <Navigate to={`/entrar?volta=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  // Sem lar: quem administra a plataforma vai para a administração; os demais, para o onboarding.
  if (!allowNoHousehold && households.length === 0)
    return (
      <Navigate
        to={
          platformRoles.length
            ? '/admin'
            : condominiums.length
              ? '/condominio'
              : professionalStatus
                ? '/prestador'
                : '/boas-vindas'
        }
        replace
      />
    );
  return children;
}

/** Faixa fixa enquanto o suporte vê o sistema como outra pessoa (PRD_SUPER_ADMIN S6). */
function ImpersonationBanner() {
  const { impersonation, user } = useSession();
  const qc = useQueryClient();
  if (!impersonation) return null;
  return (
    <div className="impersonation-banner" role="status">
      <span>
        Vendo como <strong>{user?.name}</strong> · só leitura · motivo: {impersonation.reason}
      </span>
      <button
        type="button"
        className="btn btn-sm"
        onClick={async () => {
          await api('/api/auth/impersonation/stop', { method: 'POST' }).catch(() => {});
          resetSessionCache(qc);
          window.location.assign('/admin');
        }}
      >
        Sair da visualização
      </button>
    </div>
  );
}

function GuestOnly({ children }) {
  const { loading, user } = useSession();
  const location = useLocation();
  if (loading) return <Splash />;
  if (user) {
    const back = new URLSearchParams(location.search).get('volta');
    return <Navigate to={back && back.startsWith('/') && !back.startsWith('//') ? back : '/'} replace />;
  }
  return children;
}

function NotFound() {
  return (
    <div className="state">
      <h3>Página não encontrada</h3>
      <p>O endereço pode ter mudado ou não existir.</p>
      <a className="btn" href="/">
        Voltar ao início
      </a>
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<Splash />}>
      <ImpersonationBanner />
      <Routes>
        <Route
          path="/entrar"
          element={
            <GuestOnly>
              <LoginPage />
            </GuestOnly>
          }
        />
        <Route
          path="/cadastrar"
          element={
            <GuestOnly>
              <RegisterPage />
            </GuestOnly>
          }
        />
        <Route
          path="/esqueci-senha"
          element={
            <GuestOnly>
              <ForgotPasswordPage />
            </GuestOnly>
          }
        />
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
        <Route path="/convite/:token" element={<InvitePage />} />
        {/* Fornecedor responde cotação por link, sem conta. */}
        <Route
          path="/cotacao/:token"
          element={
            <Suspense fallback={<Splash />}>
              <PublicQuotePage />
            </Suspense>
          }
        />
        {/* Só funciona quando o servidor NÃO está em produção (a API /api/dev não existe lá). */}
        <Route path="/dev/caixa-de-email" element={<MailboxPage />} />
        {/* Condomínio: painel do síndico e portal do morador. Não exige lar. */}
        <Route
          path="/condominio"
          element={
            <RequireAuth allowNoHousehold>
              <Suspense fallback={<PageFallback />}>
                <CondoIndexPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="/condominio/entrar"
          element={
            <RequireAuth allowNoHousehold>
              <Suspense fallback={<PageFallback />}>
                <JoinCondoPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="/condominio/:condoId"
          element={
            <RequireAuth allowNoHousehold>
              <Suspense fallback={<Splash />}>
                <CondoShell />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<PageFallback />}>
                <CondoHomePage />
              </Suspense>
            }
          />
          <Route
            path="estrutura"
            element={
              <Suspense fallback={<PageFallback />}>
                <StructurePage />
              </Suspense>
            }
          />
          <Route
            path="moradores"
            element={
              <Suspense fallback={<PageFallback />}>
                <ResidentsPage />
              </Suspense>
            }
          />
          <Route
            path="veiculos"
            element={
              <Suspense fallback={<PageFallback />}>
                <VehiclesPage />
              </Suspense>
            }
          />
          <Route
            path="financeiro"
            element={
              <Suspense fallback={<PageFallback />}>
                <FinanceLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <FinanceOverviewPage />
                </Suspense>
              }
            />
            <Route
              path="lancamentos"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CondoEntriesPage />
                </Suspense>
              }
            />
            <Route
              path="lancamentos/:entryId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CondoEntryDetailPage />
                </Suspense>
              }
            />
            <Route
              path="contas"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CondoAccountsPage />
                </Suspense>
              }
            />
            <Route
              path="contas/:accountId/conciliacao"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ReconciliationPage />
                </Suspense>
              }
            />
            <Route
              path="relatorios"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CondoReportsPage />
                </Suspense>
              }
            />
            <Route
              path="configuracoes"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CondoFinanceSettingsPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="cobranca"
            element={
              <Suspense fallback={<PageFallback />}>
                <BillingLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <BillingOverviewPage />
                </Suspense>
              }
            />
            <Route
              path="cobrancas"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ChargesPage />
                </Suspense>
              }
            />
            <Route
              path="cobrancas/:chargeId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ChargeDetailPage />
                </Suspense>
              }
            />
            <Route
              path="inadimplencia"
              element={
                <Suspense fallback={<PageFallback />}>
                  <DelinquencyPage />
                </Suspense>
              }
            />
            <Route
              path="acordos"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AgreementsPage />
                </Suspense>
              }
            />
            <Route
              path="antecipacao"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AntecipationPage />
                </Suspense>
              }
            />
            <Route
              path="antecipacao/:lotId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AntecipationLotPage />
                </Suspense>
              }
            />
            <Route
              path="configuracoes"
              element={
                <Suspense fallback={<PageFallback />}>
                  <BillingSettingsPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="compras"
            element={
              <Suspense fallback={<PageFallback />}>
                <PurchasingLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <PurchasingOverviewPage />
                </Suspense>
              }
            />
            <Route
              path="listas"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ListsPage />
                </Suspense>
              }
            />
            <Route
              path="listas/:listId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ListDetailPage />
                </Suspense>
              }
            />
            <Route
              path="cotacoes"
              element={
                <Suspense fallback={<PageFallback />}>
                  <RfqsPage />
                </Suspense>
              }
            />
            <Route
              path="cotacoes/:rfqId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <RfqDetailPage />
                </Suspense>
              }
            />
            <Route
              path="pedidos"
              element={
                <Suspense fallback={<PageFallback />}>
                  <OrdersPage />
                </Suspense>
              }
            />
            <Route
              path="pedidos/:orderId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <OrderDetailPage />
                </Suspense>
              }
            />
            <Route
              path="fornecedores"
              element={
                <Suspense fallback={<PageFallback />}>
                  <SuppliersPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="reservas"
            element={
              <Suspense fallback={<PageFallback />}>
                <ReservationsPage />
              </Suspense>
            }
          />
          <Route
            path="reservas/gestao"
            element={
              <Suspense fallback={<PageFallback />}>
                <ReservationsAdminPage />
              </Suspense>
            }
          />
          <Route
            path="manutencao"
            element={
              <Suspense fallback={<PageFallback />}>
                <MaintenanceLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <MaintenanceDashboard />
                </Suspense>
              }
            />
            <Route
              path="os"
              element={
                <Suspense fallback={<PageFallback />}>
                  <WorkOrdersPage />
                </Suspense>
              }
            />
            <Route
              path="os/:woId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <WorkOrderDetailPage />
                </Suspense>
              }
            />
            <Route
              path="ativos"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AssetsPage />
                </Suspense>
              }
            />
            <Route
              path="ativos/:assetId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AssetDetailPage />
                </Suspense>
              }
            />
            <Route
              path="configuracoes"
              element={
                <Suspense fallback={<PageFallback />}>
                  <MaintenanceSettingsPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="ocorrencias"
            element={
              <Suspense fallback={<PageFallback />}>
                <OccurrencesPage />
              </Suspense>
            }
          />
          <Route
            path="ocorrencias/:occId"
            element={
              <Suspense fallback={<PageFallback />}>
                <OccurrenceDetailPage />
              </Suspense>
            }
          />
          <Route
            path="comunicados"
            element={
              <Suspense fallback={<PageFallback />}>
                <AnnouncementsPage />
              </Suspense>
            }
          />
          <Route
            path="comunicados/:annId"
            element={
              <Suspense fallback={<PageFallback />}>
                <AnnouncementDetailPage />
              </Suspense>
            }
          />
          <Route
            path="documentos"
            element={
              <Suspense fallback={<PageFallback />}>
                <CondoDocumentsPage />
              </Suspense>
            }
          />
          <Route
            path="encomendas"
            element={
              <Suspense fallback={<PageFallback />}>
                <ParcelsPage />
              </Suspense>
            }
          />
          <Route
            path="assembleias"
            element={
              <Suspense fallback={<PageFallback />}>
                <AssembliesPage />
              </Suspense>
            }
          />
          <Route
            path="assembleias/:asmId"
            element={
              <Suspense fallback={<PageFallback />}>
                <AssemblyDetailPage />
              </Suspense>
            }
          />
          <Route
            path="minhas-taxas"
            element={
              <Suspense fallback={<PageFallback />}>
                <MyChargesPage />
              </Suspense>
            }
          />
          <Route
            path="indicadores"
            element={
              <Suspense fallback={<PageFallback />}>
                <IndicatorsPage />
              </Suspense>
            }
          />
          <Route
            path="prestacao-de-contas"
            element={
              <Suspense fallback={<PageFallback />}>
                <ResidentReportsPage />
              </Suspense>
            }
          />
          <Route
            path="servicos"
            element={
              <Suspense fallback={<PageFallback />}>
                <ServicesPage />
              </Suspense>
            }
          />
          <Route
            path="servicos/encontre"
            element={
              <Suspense fallback={<PageFallback />}>
                <LeadFinderPage />
              </Suspense>
            }
          />
          <Route
            path="servicos/novo"
            element={
              <Suspense fallback={<PageFallback />}>
                <NewServiceRequestPage />
              </Suspense>
            }
          />
          <Route
            path="servicos/:requestId"
            element={
              <Suspense fallback={<PageFallback />}>
                <ServiceRequestPage />
              </Suspense>
            }
          />
          <Route
            path="dia-de-manutencao"
            element={
              <Suspense fallback={<PageFallback />}>
                <MaintenanceDaysPage />
              </Suspense>
            }
          />
          <Route
            path="dia-de-manutencao/:dayId"
            element={
              <Suspense fallback={<PageFallback />}>
                <MaintenanceDayDetailPage />
              </Suspense>
            }
          />
        </Route>
        {/* Larelo: área do prestador. Qualquer conta pode se cadastrar; não exige lar. */}
        <Route
          path="/prestador"
          element={
            <RequireAuth allowNoHousehold>
              <Suspense fallback={<Splash />}>
                <ProShell />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<PageFallback />}>
                <ProProfilePage />
              </Suspense>
            }
          />
          <Route
            path="oportunidades"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProOpportunitiesPage />
              </Suspense>
            }
          />
          <Route
            path="pedidos/:requestId"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProRequestPage />
              </Suspense>
            }
          />
          <Route
            path="orcamentos"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProQuotesPage />
              </Suspense>
            }
          />
          <Route
            path="agenda"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProAgendaPage />
              </Suspense>
            }
          />
          <Route
            path="extrato"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProStatementPage />
              </Suspense>
            }
          />
          <Route
            path="avaliacoes"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProReviewsPage />
              </Suspense>
            }
          />
          <Route
            path="agenda-propria"
            element={
              <Suspense fallback={<PageFallback />}>
                <ProOwnSchedulePage />
              </Suspense>
            }
          />
        </Route>
        {/* Administração da plataforma: não exige lar; a guarda de papel fica no AdminShell. */}
        <Route
          path="/admin"
          element={
            <RequireAuth allowNoHousehold>
              <Suspense fallback={<Splash />}>
                <AdminShell />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminOverviewPage />
              </Suspense>
            }
          />
          <Route
            path="condominios"
            element={
              <Suspense fallback={<PageFallback />}>
                <CondominiumsPage />
              </Suspense>
            }
          />
          <Route
            path="condominios/novo"
            element={
              <Suspense fallback={<PageFallback />}>
                <NewCondominiumPage />
              </Suspense>
            }
          />
          <Route
            path="condominios/:condoId"
            element={
              <Suspense fallback={<PageFallback />}>
                <CondominiumDetailPage />
              </Suspense>
            }
          />
          <Route
            path="condominios/:condoId/implantacao"
            element={
              <Suspense fallback={<PageFallback />}>
                <CondoOnboardingPage />
              </Suspense>
            }
          />
          <Route
            path="financeiro"
            element={
              <Suspense fallback={<PageFallback />}>
                <OperatorFinancePage />
              </Suspense>
            }
          />
          <Route
            path="antecipacao"
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminAntecipationPage />
              </Suspense>
            }
          />
          <Route
            path="antecipacao/:lotId"
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminLotPage />
              </Suspense>
            }
          />
          <Route
            path="organizacoes"
            element={
              <Suspense fallback={<PageFallback />}>
                <OrganizationsPage />
              </Suspense>
            }
          />
          <Route
            path="planos"
            element={
              <Suspense fallback={<PageFallback />}>
                <PlansPage />
              </Suspense>
            }
          />
          <Route
            path="integracoes"
            element={
              <Suspense fallback={<PageFallback />}>
                <IntegrationsPage />
              </Suspense>
            }
          />
          <Route
            path="administradores"
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminsPage />
              </Suspense>
            }
          />
          <Route
            path="auditoria"
            element={
              <Suspense fallback={<PageFallback />}>
                <AuditPage />
              </Suspense>
            }
          />
          <Route
            path="larelo"
            element={
              <Suspense fallback={<PageFallback />}>
                <LareloAdminPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/boas-vindas"
          element={
            <RequireAuth allowNoHousehold>
              <OnboardingPage />
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<PageFallback />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="tarefas"
            element={
              <Suspense fallback={<PageFallback />}>
                <TasksPage />
              </Suspense>
            }
          />
          <Route
            path="tarefas/revisao"
            element={
              <Suspense fallback={<PageFallback />}>
                <WeeklyReviewPage />
              </Suspense>
            }
          />
          <Route
            path="tarefas/:taskId"
            element={
              <Suspense fallback={<PageFallback />}>
                <TaskDetailPage />
              </Suspense>
            }
          />
          <Route
            path="compras"
            element={
              <Suspense fallback={<PageFallback />}>
                <ShoppingPage />
              </Suspense>
            }
          />
          <Route
            path="compras/:listId"
            element={
              <Suspense fallback={<PageFallback />}>
                <ShoppingPage />
              </Suspense>
            }
          />
          <Route
            path="despensa"
            element={
              <Suspense fallback={<PageFallback />}>
                <PantryPage />
              </Suspense>
            }
          />
          <Route
            path="refeicoes"
            element={
              <Suspense fallback={<PageFallback />}>
                <MealsPage />
              </Suspense>
            }
          />
          <Route
            path="refeicoes/receitas/:recipeId"
            element={
              <Suspense fallback={<PageFallback />}>
                <RecipePage />
              </Suspense>
            }
          />
          <Route
            path="financas"
            element={
              <Suspense fallback={<PageFallback />}>
                <FinancePage />
              </Suspense>
            }
          />
          <Route
            path="financas/lancamentos/:entryId"
            element={
              <Suspense fallback={<PageFallback />}>
                <FinancePage />
              </Suspense>
            }
          />
          <Route
            path="agenda"
            element={
              <Suspense fallback={<PageFallback />}>
                <AgendaPage />
              </Suspense>
            }
          />
          <Route
            path="agenda/:eventId"
            element={
              <Suspense fallback={<PageFallback />}>
                <AgendaPage />
              </Suspense>
            }
          />
          <Route
            path="manutencao"
            element={
              <Suspense fallback={<PageFallback />}>
                <MaintenancePage />
              </Suspense>
            }
          />
          <Route
            path="manutencao/:assetId"
            element={
              <Suspense fallback={<PageFallback />}>
                <AssetPage />
              </Suspense>
            }
          />
          <Route
            path="documentos"
            element={
              <Suspense fallback={<PageFallback />}>
                <DocumentsPage />
              </Suspense>
            }
          />
          <Route
            path="entrada"
            element={
              <Suspense fallback={<PageFallback />}>
                <InboxPage />
              </Suspense>
            }
          />
          {/* Destino do "Compartilhar" do celular (share_target do manifest). */}
          <Route
            path="receber"
            element={
              <Suspense fallback={<PageFallback />}>
                <ShareTargetPage />
              </Suspense>
            }
          />
          <Route
            path="notificacoes"
            element={
              <Suspense fallback={<PageFallback />}>
                <NotificationsPage />
              </Suspense>
            }
          />
          <Route
            path="configuracoes/lar"
            element={
              <Suspense fallback={<PageFallback />}>
                <HouseholdSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="configuracoes/conta"
            element={
              <Suspense fallback={<PageFallback />}>
                <AccountSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="mais"
            element={
              <Suspense fallback={<PageFallback />}>
                <MorePage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
